/**
 * Vários microfones (spec 016) — preferência DESTE computador (localStorage,
 * por perfil do Chrome, como o Motor da escuta). Desligado por padrão:
 * desligado, todo o sistema abre o microfone padrão como sempre abriu.
 *
 * Cada microfone escolhido guarda quem ele ouve (médico/paciente/os dois),
 * onde fica, volume e corte de ruído. Aparelho "dividido" (receptor que põe
 * um transmissor em cada canal) vira duas entradas: `id#L` e `id#R`.
 */

import { useSyncExternalStore } from 'react';
import type { MicDevice } from './devices';
import { LIMIAR_FALANTE_PADRAO_DB } from './falante';

const LS_KEY = 'dratento.voice.mics.v1';

export type Quem = 'medico' | 'paciente' | 'ambos';
export type Canal = 'L' | 'R';

export const LOCAIS = ['Lapela', 'Lâmpada de fenda', 'Mesa de consulta', 'Refrator', 'Outro'] as const;

export interface MicConfig {
  /** `deviceId` do aparelho (o mesmo nas duas metades de um dividido). */
  id: string;
  /** Nome quando foi escolhido — reencontra o aparelho se o Chrome trocar o id. */
  nome: string;
  /** Metade de um aparelho dividido; sem = o aparelho inteiro. */
  canal?: Canal;
  usar: boolean;
  quem: Quem;
  /** Um de `LOCAIS`, ou '' (não disse). */
  onde: string;
  /** −12 a +12 dB. */
  volumeDb: number;
  /** Corte de ruído 0–10 (`dsp.corteDb`). */
  corte: number;
}

export interface MicsPrefs {
  ativo: boolean;
  mics: MicConfig[];
  /** Aparelhos divididos em canal esquerdo/direito. */
  divididos: string[];
  /** Quanto a lapela tem que estar acima da sala pra contar como médico (dB). Sem = padrão. */
  limiarFalanteDb?: number;
}

export const CORTE_PADRAO = 3;
const VAZIO: MicsPrefs = { ativo: false, mics: [], divididos: [], limiarFalanteDb: LIMIAR_FALANTE_PADRAO_DB };

export function chaveMic(m: { id: string; canal?: Canal }): string {
  return m.canal ? `${m.id}#${m.canal}` : m.id;
}

export function micPadrao(dev: MicDevice, canal?: Canal): MicConfig {
  return { id: dev.id, nome: dev.nome, ...(canal ? { canal } : {}), usar: false, quem: 'ambos', onde: '', volumeDb: 0, corte: CORTE_PADRAO };
}

const num = (v: unknown, min: number, max: number, padrao: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : padrao;

/** Lê o que estiver gravado sem confiar no formato (versão antiga, edição à mão). */
export function parsePrefs(raw: string | null): MicsPrefs {
  if (!raw) return VAZIO;
  try {
    const p = JSON.parse(raw) as Partial<Record<keyof MicsPrefs, unknown>>;
    const mics: MicConfig[] = [];
    for (const m of Array.isArray(p.mics) ? p.mics : []) {
      if (!m || typeof m !== 'object') continue;
      const o = m as Record<string, unknown>;
      if (typeof o.id !== 'string' || !o.id) continue;
      const canal = o.canal === 'L' || o.canal === 'R' ? o.canal : undefined;
      mics.push({
        id: o.id,
        nome: typeof o.nome === 'string' ? o.nome : '',
        ...(canal ? { canal } : {}),
        usar: o.usar === true,
        quem: o.quem === 'medico' || o.quem === 'paciente' ? o.quem : 'ambos',
        onde: typeof o.onde === 'string' ? o.onde : '',
        volumeDb: num(o.volumeDb, -12, 12, 0),
        corte: num(o.corte, 0, 10, CORTE_PADRAO),
      });
    }
    const divididos = Array.isArray(p.divididos) ? p.divididos.filter((x): x is string => typeof x === 'string') : [];
    return { ativo: p.ativo === true, mics, divididos, limiarFalanteDb: num(p.limiarFalanteDb, 0, 20, LIMIAR_FALANTE_PADRAO_DB) };
  } catch {
    return VAZIO;
  }
}

export interface MicResolvido {
  dev: MicDevice;
  cfg: MicConfig;
}

/**
 * Casa a preferência com os aparelhos ligados agora. Pelo id; se o Chrome
 * trocou o id (dados do site limpos), pelo nome — só se o nome for único
 * dos dois lados, senão não dá pra saber qual é qual. Aparelho sem
 * preferência entra com o padrão (fora de uso).
 */
export function resolveMics(prefs: MicsPrefs, devs: readonly MicDevice[]): MicResolvido[] {
  const porId = new Map<string, MicConfig[]>();
  for (const m of prefs.mics) porId.set(m.id, [...(porId.get(m.id) ?? []), m]);
  const contar = (nomes: string[]) => nomes.reduce((acc, n) => acc.set(n, (acc.get(n) ?? 0) + 1), new Map<string, number>());
  const nomesDev = contar(devs.map((d) => d.nome));
  const nomesCfg = contar([...porId.values()].map((ms) => ms[0].nome));
  const idsLigados = new Set(devs.map((d) => d.id));
  const out: MicResolvido[] = [];
  for (const dev of devs) {
    let cfgs = porId.get(dev.id);
    let idAntigo = dev.id;
    if (!cfgs && nomesDev.get(dev.nome) === 1 && nomesCfg.get(dev.nome) === 1) {
      for (const [id, ms] of porId) {
        if (!idsLigados.has(id) && ms[0].nome === dev.nome) {
          cfgs = ms;
          idAntigo = id;
          break;
        }
      }
    }
    const dividido = prefs.divididos.includes(idAntigo);
    const canais: (Canal | undefined)[] = dividido ? ['L', 'R'] : [undefined];
    for (const canal of canais) {
      const achado = cfgs?.find((c) => c.canal === canal);
      out.push({ dev, cfg: achado ? { ...achado, id: dev.id, nome: dev.nome } : micPadrao(dev, canal) });
    }
  }
  return out;
}

let prefs: MicsPrefs = VAZIO;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function ler(): MicsPrefs {
  try {
    return parsePrefs(localStorage.getItem(LS_KEY));
  } catch {
    return VAZIO;
  }
}

function gravar(next: MicsPrefs): void {
  prefs = next;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    /* storage bloqueado: vale só nesta sessão */
  }
  emit();
}

if (typeof window !== 'undefined') {
  prefs = ler();
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY) return;
    prefs = ler();
    emit();
  });
}

export function getMicsPrefs(): MicsPrefs {
  return prefs;
}

export function setMicsAtivo(ativo: boolean): void {
  gravar({ ...prefs, ativo });
}

/** Grava a preferência de um microfone. Marcar "médico" tira o médico de outro (só um por vez). */
export function salvarMic(cfg: MicConfig): void {
  const k = chaveMic(cfg);
  const outros = prefs.mics
    .filter((m) => chaveMic(m) !== k)
    .map((m) => (cfg.quem === 'medico' && m.quem === 'medico' ? { ...m, quem: 'ambos' as Quem } : m));
  gravar({ ...prefs, mics: [...outros, cfg] });
}

export function setLimiarFalante(db: number): void {
  gravar({ ...prefs, limiarFalanteDb: Math.min(20, Math.max(0, Math.round(db))) });
}

/**
 * Dá pra saber quem fala (fase 2)? Um microfone "Médico" em uso e pelo
 * menos mais um em uso — senão não há com quem comparar a lapela.
 */
export function separaFalante(usados: readonly { quem: Quem }[]): boolean {
  return usados.filter((m) => m.quem === 'medico').length === 1 && usados.length >= 2;
}

/** Divide o aparelho em canal esquerdo/direito, ou junta de novo (as metades somem). */
export function setDividido(dev: MicDevice, dividir: boolean): void {
  const divididos = prefs.divididos.filter((id) => id !== dev.id);
  const mics = prefs.mics.filter((m) => m.id !== dev.id);
  gravar({ ...prefs, divididos: dividir ? [...divididos, dev.id] : divididos, mics });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useMicsPrefs(): MicsPrefs {
  return useSyncExternalStore(subscribe, () => prefs, () => VAZIO);
}
